// @flow

import React from 'react';
import type { Node } from 'react';
import { Row, Col } from '@riseart/antd-provider';
import { LazyloadPlaceholder, MediaQuery } from '@riseart/common';
import { GridHeader, GridCounter } from '@riseart/grid';
import { SliderPaginationCounter } from 'shared_components/common/pagination/counter/Slider';
import { IsomorphicRipple } from 'shared_components/common/preloader/IsomorphicRipple';
import { EventCard } from 'shared_components/event/Card';
import { EventsRsvpProvider } from 'shared_data/providers/rsvp/Event';
import { CreateRsvpProvider } from 'shared_data/providers/rsvp/CreateRsvp';

import { raScreenXsMax, raScreenSmMax } from '@riseart/antd-provider/dist/website/variables.less';

type Props = {
  items: Array<Object>,
  isLoading: boolean,
  pagination: {
    currentPage: number,
    itemsPerPage: number,
    totalItems: number,
  },
};

/**
 * EventList
 *
 * @param {Props} props
 * @returns {Node}
 */
export const EventList = ({ items, isLoading, pagination }: Props): Node => (
  <IsomorphicRipple isActive={isLoading}>
    {pagination ? (
      <GridHeader>
        <span />
        <GridCounter>
          <SliderPaginationCounter
            currentPage={pagination.currentPage}
            itemsPerPage={pagination.itemsPerPage}
            totalItems={pagination.totalItems}
          />
        </GridCounter>
      </GridHeader>
    ) : null}
    {items && items.length ? (
      <EventsRsvpProvider>
        <CreateRsvpProvider eventsList={items} />
        <MediaQuery maxWidth={raScreenXsMax}>
          {(isSmallScreen) => (
            <MediaQuery maxWidth={raScreenSmMax}>
              {(isMediumScreen) => {
                // Lazyload images after the first row
                let lazyloadAfterIndex = 2;

                if (isSmallScreen) {
                  lazyloadAfterIndex = 0;
                } else if (isMediumScreen) {
                  lazyloadAfterIndex = 1;
                }

                return (
                  <Row gutter={[30, 100]}>
                    {items.map((item, idx) => (
                      <Col xs={24} sm={12} md={8} key={item.id}>
                        <EventCard
                          event={item}
                          lazyloadConfig={
                            idx > lazyloadAfterIndex
                              ? {
                                  once: true,
                                  height: 260,
                                  offset: 200,
                                  placeholder: <LazyloadPlaceholder height={260} />,
                                }
                              : null
                          }
                          artDirectionSizeKey="grid.flat4Col"
                        />
                      </Col>
                    ))}
                  </Row>
                );
              }}
            </MediaQuery>
          )}
        </MediaQuery>
      </EventsRsvpProvider>
    ) : null}
  </IsomorphicRipple>
);
